import { Alert } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  CreatePartnerFormValues,
  getCreatePartnerDefaultValues,
  mapCreatePartnerResponseErrors,
  toCreatePartnerPayload,
  useCreatePartnerForm,
  validateCreatePartnerForm,
} from 'src/components/CreatePartnerModal/use-create-partner-form';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { useCreatePartnerWithRelatedData } from 'src/graphql/entities/partner/partner';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useConfirmationModal } from 'src/utils/hooks';

export const CreatePartnerModal = () => {
  const { closeModal } = useModalMethods(Modals.CreatePartnerModal);
  const { isOpened } = useModalState(Modals.CreatePartnerModal);
  const { showModal: showSelectPartnerModal } = useModalMethods(Modals.SelectPartnerModal);
  const { createPartnerWithRelatedData, loading } = useCreatePartnerWithRelatedData();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { confirm } = useConfirmationModal();

  const formMethods = useForm<CreatePartnerFormValues>({
    defaultValues: getCreatePartnerDefaultValues(),
    mode: 'onChange',
  });

  const { clearErrors, formState, handleSubmit, reset, setError } = formMethods;

  const createPartnerFormFields = useCreatePartnerForm({
    formMethods,
    isSubmitting: loading,
  });

  const returnToSelector = useCallback(() => {
    reset(getCreatePartnerDefaultValues());
    closeModal();
    showSelectPartnerModal(null);
  }, [closeModal, reset, showSelectPartnerModal]);

  const handleCloseRequest = useCallback(async () => {
    if (loading) {
      return;
    }

    if (!formState.isDirty) {
      returnToSelector();
      return;
    }

    const shouldDiscard = await confirm({
      title: 'Discard changes?',
      body: 'The partner details you entered will be lost.',
      confirmLabel: 'Discard changes',
      cancelLabel: 'Keep editing',
      danger: true,
    });

    if (shouldDiscard) {
      returnToSelector();
    }
  }, [confirm, formState.isDirty, loading, returnToSelector]);

  const onSubmit = handleSubmit(async (values) => {
    clearErrors();
    setSubmitError(null);

    const isValid = validateCreatePartnerForm(values, setError);
    if (!isValid) {
      return;
    }

    const response = await createPartnerWithRelatedData(toCreatePartnerPayload(values));

    if (response.errors.length > 0 || !response.partner) {
      mapCreatePartnerResponseErrors(response.errors, setError, setSubmitError);
      return;
    }

    returnToSelector();
  });

  return (
    <ModalContainer
      open={isOpened}
      size="large"
      title="Create partner"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'create-partner-cancel' },
        {
          label: 'Create partner',
          kind: 'primary',
          onClick: () => void onSubmit(),
          loading,
          loadingLabel: 'Creating partner…',
          testId: 'create-partner-submit',
        },
      ]}
      onClose={() => void handleCloseRequest()}
      data-testid="create-partner-modal"
    >
      <GenericForm<CreatePartnerFormValues> formMethods={formMethods}>
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}
        {createPartnerFormFields}
      </GenericForm>
    </ModalContainer>
  );
};
